import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, User } from 'lucide-react';

export function RegisterModal({ open, onOpenChange, onSuccess }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, loginWithGoogle } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Las contrasenas no coinciden');
      return;
    }
    if (password.length < 6) {
      toast.error('La contrasena debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      const referralCode = localStorage.getItem('klarvi_referral_code');
      const data = await register(name, email, password, referralCode || undefined);
      localStorage.removeItem('klarvi_referral_code');
      toast.success('Cuenta creada exitosamente');
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'sign_up', { method: 'email' });
        window.gtag('event', 'conversion', {
          send_to: 'AW-18303994422/_iJ2COH4quscELaUg5hE',
          value: 1,
          currency: 'MXN',
        });
      }
      onOpenChange(false);
      onSuccess?.(data.user);
    } catch (error) {
      toast.error(error.message || 'Error al crear cuenta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="register-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-['Manrope'] text-center">
            Crea tu cuenta para ver tu resultado
          </DialogTitle>
          <DialogDescription className="text-center">
            Tus datos ya estan listos. Registrate y obten tu resultado al instante.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="modal-name">Nombre Completo</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="modal-name"
                type="text"
                placeholder="Tu nombre"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="pl-10"
                required
                data-testid="modal-name-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="modal-email">Correo Electronico</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="modal-email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                required
                data-testid="modal-email-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="modal-password">Contrasena</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="modal-password"
                type="password"
                placeholder="********"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10"
                required
                data-testid="modal-password-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="modal-confirm">Confirmar Contrasena</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="modal-confirm"
                type="password"
                placeholder="********"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-10"
                required
                data-testid="modal-confirm-input"
              />
            </div>
          </div>
          <Button
            type="submit"
            className="w-full bg-[#0F172A] hover:bg-[#0F172A]/90"
            disabled={loading}
            data-testid="modal-register-btn"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Crear Cuenta y Ver Resultado
          </Button>
        </form>

        <div className="relative my-2">
          <Separator />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-xs text-muted-foreground">
            o continua con
          </span>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={loginWithGoogle}
          data-testid="modal-google-btn"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Google
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          Ya tienes cuenta?{' '}
          <Link to="/login" className="text-[#2563EB] hover:underline font-medium" data-testid="modal-login-link">
            Iniciar sesion
          </Link>
        </p>
      </DialogContent>
    </Dialog>
  );
}
